import { Injectable } from '@angular/core';
import { ConfigurationsService } from '@epicuro-next/platform/configuration';
import {
  selectExtendedDeliveryShops,
  submitBasketSuccess,
  updateBasketItems,
} from '@epicuro-next/state/basket-state';
import { shopTheList } from '@epicuro-next/state/product-list-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { arrayIsEmpty } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, select, Store } from '@ngrx/store';
import { OrderType } from 'graphql-api/gql-api-interfaces';
import { combineLatest, EMPTY, of } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import {
  ConfigStripStateConfigurationKeys,
  ConfigStripStateConfigurations,
  FEATURE_NAME,
} from '../../configurations';
import {
  clearPrefilledDeliveryItems,
  copyPrefilledDeliveryItems,
  loadProductSuccess,
  openCSForList,
  openCSForPBasket,
  openCSFromCbasket,
  setPrefilledDeliveryItems,
  setPrefilledVisible,
} from '../actions/config-strip.actions';
import {
  selectAvailableVariants,
  selectCachedDeliveryPeriods,
  selectDataConfigStripState,
  selectMasterQuantity,
  selectPrefilledDeliveryItemsCategory,
} from '../selectors';
import { getScales } from '../shared/scales';

@Injectable()
export class ConfigStripPrefilledEffects {
  private categoryField$ = this.configService.getConfigValue<
    ConfigStripStateConfigurations,
    ConfigStripStateConfigurationKeys.CategoryField
  >({
    featureName: ConfigStripStateConfigurationKeys.CategoryField,
    defaultValue: 'category',
    namespace: FEATURE_NAME,
  });
  /**
   * Remembers currently selected master quantities into store/state
   * so at the next reload it can set master quantities for matched
   * sizes (category)
   */
  public updatePrefilledOnUpdateBasketRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBasketItems),
      concatLatestFrom(() => [
        this.categoryField$,
        this.store.select(selectDataConfigStripState),
      ]),
      switchMap(([_, categoryField, data]) => {
        const [sizeField, scaleField] = getScales(data);
        /**
         * ecco products, for instance, do not have category so we map them by division
         * brooks products take experience field as category
         */
        const category = data.product?.attributes?.[categoryField] as
          | string
          | undefined;

        if (!category) {
          return EMPTY;
        }

        return of(
          setPrefilledDeliveryItems({
            category,
            scaleField,
            sizeField,
            sizes: data.sizes,
          }),
        );
      }),
    ),
  );

  public setPrefilledVisible$ = createEffect(() =>
    this.actions$.pipe(
      ofType(shopTheList),
      map(() => setPrefilledVisible({ visible: true })),
    ),
  );

  public setPrefilledNotVisible$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openCSForPBasket, openCSForList, openCSFromCbasket),
      map(() => setPrefilledVisible({ visible: false })),
    ),
  );

  public clearPrefilledDeliveryItemsOnSubmitBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitBasketSuccess),
      map(() => clearPrefilledDeliveryItems()),
    ),
  );

  private copyPrefilledDeliveryPeriodsToDeliveryItems$ = combineLatest([
    this.store.pipe(select(selectCachedDeliveryPeriods), take(1)),
    this.store.pipe(
      select(selectExtendedDeliveryShops),
      filter((v) => !arrayIsEmpty(v)),
      take(1),
    ),
    this.store.pipe(
      select(selectAvailableVariants),
      filter((v) => !arrayIsEmpty(v)),
      take(1),
    ),
  ]).pipe(
    withLatestFrom(this.store.select(selectDataConfigStripState)),
    map(([[cachedDeliveryPeriods, shops, variants], data]) => {
      const [sizeField, scaleField] = getScales(data);

      return copyPrefilledDeliveryItems({
        periods: cachedDeliveryPeriods.concat([OrderType.Now]),
        shops,
        variants,
        sizeField,
        scaleField,
        data,
      });
    }),
  );

  public prefilledDeliveryItemsOnLoadProductSuccessStrategy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadProductSuccess),
        withLatestFrom(
          this.store.select(selectPrefilledDeliveryItemsCategory),
          this.store.select(selectMasterQuantity),
          this.categoryField$,
        ),
        switchMap(
          ([
            {
              product: { attributes },
            },
            prefilledCategory,
            masterQuantities,
            categoryField,
          ]) => {
            if (
              !!prefilledCategory &&
              prefilledCategory !== attributes?.[categoryField]
            ) {
              const actions: Action[] = [clearPrefilledDeliveryItems()];

              if (!arrayIsEmpty(Object.values(masterQuantities ?? {}))) {
                actions.push(
                  showSnackBarMessage({
                    message: $localize`:ConfigStrip|Master quantities cleared on category change@@configStripMasterQuantities:Cleared master quantities`,
                    messageType: MessageType.INFO,
                  }),
                );
              }

              return actions;
            }

            return this.copyPrefilledDeliveryPeriodsToDeliveryItems$;
          },
        ),
      ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private configService: ConfigurationsService,
  ) {}
}
